import type { ComponentChildren } from 'preact';

interface BaseProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
}

const describedBy = (id: string, error?: string) => (error ? `${id}-err` : undefined);

function Label({ id, label, required }: BaseProps) {
  return (
    <label class="label" for={id}>
      {label}
      {required ? null : <span class="req"> (optional)</span>}
    </label>
  );
}

function ErrorText({ id, error }: { id: string; error?: string }) {
  return error ? (
    <span class="err" id={`${id}-err`}>
      {error}
    </span>
  ) : null;
}

interface TextProps extends BaseProps {
  value: string;
  type?: 'text' | 'email' | 'tel';
  autocomplete?: string;
  onInput: (value: string) => void;
  onBlur?: () => void;
}

export function TextField(p: TextProps) {
  return (
    <div class="field">
      <Label {...p} />
      <input
        class="input"
        id={p.id}
        name={p.id}
        // Preact 11 discriminates input props on `type`; text/email/tel share the same shape.
        {...({ type: p.type ?? 'text' } as { type: 'text' })}
        value={p.value}
        autocomplete={p.autocomplete}
        required={p.required}
        aria-required={p.required ? 'true' : undefined}
        aria-invalid={p.error ? 'true' : undefined}
        aria-describedby={describedBy(p.id, p.error)}
        onInput={(e) => p.onInput((e.currentTarget as HTMLInputElement).value)}
        onBlur={p.onBlur}
      />
      <ErrorText id={p.id} error={p.error} />
    </div>
  );
}

interface SelectProps extends BaseProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

export function SelectField(p: SelectProps) {
  return (
    <div class="field">
      <Label {...p} />
      <select
        class="input"
        id={p.id}
        name={p.id}
        value={p.value}
        aria-required={p.required ? 'true' : undefined}
        aria-invalid={p.error ? 'true' : undefined}
        aria-describedby={describedBy(p.id, p.error)}
        onChange={(e) => p.onChange((e.currentTarget as HTMLSelectElement).value)}
      >
        <option value="">Select…</option>
        {p.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ErrorText id={p.id} error={p.error} />
    </div>
  );
}

interface CheckProps extends Omit<BaseProps, 'label'> {
  label: ComponentChildren;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function CheckField(p: CheckProps) {
  return (
    <div class="field">
      <label class="check" for={p.id}>
        <input
          type="checkbox"
          id={p.id}
          name={p.id}
          checked={p.checked}
          aria-required={p.required ? 'true' : undefined}
          aria-invalid={p.error ? 'true' : undefined}
          aria-describedby={describedBy(p.id, p.error)}
          onChange={(e) => p.onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>{p.label}</span>
      </label>
      <ErrorText id={p.id} error={p.error} />
    </div>
  );
}
